import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '../../ui/button';

interface MetadataItem {
  label: string;
  value: string | null;
  date?: boolean;
}

interface MetadataPanelProps {
  metadata: MetadataItem[];
  isExpanded?: boolean;
  onToggle?: (expanded: boolean) => void;
}

const MetadataPanel: React.FC<MetadataPanelProps> = ({
  metadata,
  isExpanded: controlledExpanded,
  onToggle,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isExpanded =
    controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const setIsExpanded = onToggle || setInternalExpanded;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const hasData = metadata.some((item) => item.value);

  if (!hasData) {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <Button
        variant="ghost"
        onClick={handleToggle}
        className="w-full justify-between p-3 text-left hover:bg-gray-50"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            Intake Metadata
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
      </Button>

      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          <div className="space-y-2 pt-2">
            {metadata.map((item, index) => (
              <div
                key={index}
                className="flex justify-between items-start text-xs"
              >
                <span className="text-gray-600 font-medium">{item.label}:</span>
                <span className="text-gray-900 text-right max-w-[60%]">
                  {item.value ? (
                    item.date ? (
                      formatDate(item.value)
                    ) : (
                      item.value
                    )
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetadataPanel;
